import {
    useCallback,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    Platform,
    type View
} from "react-native";
import type IQuestionnaireProps from "./type";
import type {
    QuestionnaireChoiceChangeParams,
    QuestionnaireKeyboardEventType,
    QuestionnaireInputChangeParams,
    QuestionnaireChoiceMoveParams,
    QuestionnaireContextType,
    QuestionnaireFocusable,
    QuestionnaireAnswers
} from "./type";
import QuestionnaireActions from "./components/actions";
import QuestionnaireChoice from "./components/choice";
import QuestionnaireChoices from "./components/choices";
import QuestionnaireDescription from "./components/description";
import QuestionnaireError from "./components/error";
import QuestionnaireInput from "./components/input";
import QuestionnaireItem from "./components/item";
import QuestionnaireProgress from "./components/progress";
import QuestionnaireReview from "./components/review";
import QuestionnaireStatus from "./components/status";
import QuestionnaireTitle from "./components/title";
import {
    QuestionnaireContext
} from "./context";
import {
    getQuestionnaireSelectBehaviour,
    getQuestionnaireChoiceShortcut,
    getQuestionnaireCleanAnswers,
    QUESTIONNAIRE_EMPTY_ANSWERS,
    getQuestionnaireHasModifier,
    getQuestionnaireNextAnswer,
    getQuestionnaireTextAnswer,
    getQuestionnaireIsAnswered,
    QUESTIONNAIRE_EMPTY_ITEMS,
    getQuestionnaireNextIndex,
    getQuestionnaireSwapStep,
    QUESTIONNAIRE_BUTTON_TAG,
    QUESTIONNAIRE_TEXT_TAGS,
    QUESTIONNAIRE_ENTER_KEY,
    getQuestionnaireTagName
} from "./util";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import Card from "../card";

const Questionnaire: FC<IQuestionnaireProps> = ({
    defaultAnswers = QUESTIONNAIRE_EMPTY_ANSWERS,
    items = QUESTIONNAIRE_EMPTY_ITEMS,
    activeIndex: activeIndexProp,
    isReviewEnabled = true,
    isSubmitFailed = false,
    isSubmitted = false,
    onActiveIndexChange,
    isDisabled = false,
    isLoading = false,
    submitErrorText,
    customLocalize,
    size: sizeProp,
    customTheme,
    submittedAt,
    children,
    onSubmit,
    style,
    title,
    ...props
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        answers,
        setAnswers
    ] = useState<QuestionnaireAnswers>(defaultAnswers);

    const [
        internalIndex,
        setInternalIndex
    ] = useState(0);

    const [
        errorName,
        setErrorName
    ] = useState<string | null>(null);

    const [
        isReturning,
        setIsReturning
    ] = useState(false);

    const choiceRefs = useRef<Array<QuestionnaireFocusable | null>>([]);

    const inputRef = useRef<QuestionnaireFocusable | null>(null);

    const contentRef = useRef<View | null>(null);

    const isFocusPendingRef = useRef(false);

    const size = sizeProp ?? (pointerType === "coarse" ? "large" : "medium");

    const itemCount = items.length;

    const maxIndex = isReviewEnabled ? itemCount : Math.max(0, itemCount - 1);

    const activeIndex = Math.min(Math.max(0, activeIndexProp ?? internalIndex), maxIndex);

    const isReview = isReviewEnabled && activeIndex >= itemCount;

    const activeItem = isReview ? undefined : items[activeIndex];

    const isLocked = isLoading || isDisabled || isSubmitted;

    const previousIndexRef = useRef(activeIndex);

    const swapStep = getQuestionnaireSwapStep({
        previousIndex: previousIndexRef.current,
        index: activeIndex
    });

    useEffect(() => {
        previousIndexRef.current = activeIndex;
    }, [
        activeIndex
    ]);

    useEffect(() => {
        if(!isFocusPendingRef.current) {
            return;
        }

        isFocusPendingRef.current = false;

        contentRef.current?.focus();
    }, [
        activeIndex
    ]);

    const isSubmittedRef = useRef(isSubmitted);

    useEffect(() => {
        if(isSubmitted && !isSubmittedRef.current) {
            contentRef.current?.focus();
        }

        isSubmittedRef.current = isSubmitted;
    }, [
        isSubmitted
    ]);

    const registerChoice = useCallback((index: number, node: QuestionnaireFocusable | null) => {
        choiceRefs.current[index] = node;
    }, []);

    const registerInput = useCallback((node: QuestionnaireFocusable | null) => {
        inputRef.current = node;
    }, []);

    const handleIndexChange = (index: number) => {
        isFocusPendingRef.current = true;

        setErrorName(null);

        setInternalIndex(index);

        onActiveIndexChange?.(index);
    };

    const submitAnswers = (nextAnswers: QuestionnaireAnswers) => {
        if(isLocked) {
            return;
        }

        onSubmit?.(getQuestionnaireCleanAnswers({
            answers: nextAnswers,
            items
        }));
    };

    const handleSubmit = useStableCallback(() => {
        submitAnswers(answers);
    });

    const handleAdvance = (nextAnswers: QuestionnaireAnswers) => {
        if(isReturning && isReviewEnabled) {
            setIsReturning(false);

            handleIndexChange(itemCount);

            return;
        }

        if(activeIndex < itemCount - 1) {
            handleIndexChange(activeIndex + 1);

            return;
        }

        if(isReviewEnabled) {
            handleIndexChange(itemCount);

            return;
        }

        submitAnswers(nextAnswers);
    };

    const handleFocusInvalid = (item: NonNullable<typeof activeItem>) => {
        const firstIndex = (item.choices ?? []).findIndex((choice) => !choice.isDisabled);

        const node = firstIndex >= 0 ? choiceRefs.current[firstIndex] : inputRef.current;

        (node ?? inputRef.current)?.focus();
    };

    const handleNext = useStableCallback(() => {
        if(isLocked) {
            return;
        }

        if(isReview) {
            handleSubmit();

            return;
        }

        if(!activeItem) {
            return;
        }

        if(activeItem.isRequired && !getQuestionnaireIsAnswered(answers[activeItem.name])) {
            setErrorName(activeItem.name);

            handleFocusInvalid(activeItem);

            return;
        }

        handleAdvance(answers);
    });

    const handleSkip = useStableCallback(() => {
        if(isLocked || !activeItem || activeItem.isRequired) {
            return;
        }

        const nextAnswers = {
            ...answers
        };

        delete nextAnswers[activeItem.name];

        setAnswers(nextAnswers);

        handleAdvance(nextAnswers);
    });

    const handleBack = useStableCallback(() => {
        if(isLocked) {
            return;
        }

        setIsReturning(false);

        handleIndexChange(isReview ? itemCount - 1 : Math.max(0, activeIndex - 1));
    });

    const handleEdit = useStableCallback((index: number) => {
        if(isLocked) {
            return;
        }

        setIsReturning(true);

        handleIndexChange(index);
    });

    const handleChoiceChange = useStableCallback(({
        value,
        item
    }: QuestionnaireChoiceChangeParams) => {
        if(isLocked) {
            return;
        }

        setAnswers((previous) => ({
            ...previous,
            [item.name]: getQuestionnaireNextAnswer({
                answer: previous[item.name],
                item,
                value
            })
        }));

        setErrorName((previous) => previous === item.name ? null : previous);
    });

    const handleInputChange = useStableCallback(({
        item,
        text
    }: QuestionnaireInputChangeParams) => {
        if(isLocked) {
            return;
        }

        setAnswers((previous) => ({
            ...previous,
            [item.name]: getQuestionnaireTextAnswer({
                answer: previous[item.name],
                item,
                text
            })
        }));

        setErrorName((previous) => previous === item.name ? null : previous);
    });

    const handleChoiceMove = useStableCallback(({
        index,
        item,
        step
    }: QuestionnaireChoiceMoveParams) => {
        const nextIndex = getQuestionnaireNextIndex({
            index,
            item,
            step
        });

        const nextChoice = item.choices?.[nextIndex];

        if(!nextChoice) {
            return;
        }

        choiceRefs.current[nextIndex]?.focus();

        handleChoiceChange({
            value: nextChoice.value,
            item
        });
    });

    const handleShortcut = (event: QuestionnaireKeyboardEventType) => {
        if(!activeItem || getQuestionnaireSelectBehaviour(activeItem) === "none") {
            return;
        }

        const choices = activeItem.choices ?? [];

        const index = choices.findIndex((choice, choiceIndex) => getQuestionnaireChoiceShortcut({
            index: choiceIndex,
            choice
        }).toLowerCase() === event.key.toLowerCase());

        const choice = choices[index];

        if(!choice || choice.isDisabled) {
            return;
        }

        event.preventDefault();

        choiceRefs.current[index]?.focus();

        handleChoiceChange({
            value: choice.value,
            item: activeItem
        });
    };

    const onKeyDown = (event: QuestionnaireKeyboardEventType) => {
        if(isLocked || event.defaultPrevented || getQuestionnaireHasModifier(event)) {
            return;
        }

        const tagName = getQuestionnaireTagName(event.target);

        if(event.key === QUESTIONNAIRE_ENTER_KEY) {
            if(tagName === QUESTIONNAIRE_BUTTON_TAG || event.shiftKey || event.nativeEvent?.isComposing) {
                return;
            }

            event.preventDefault();

            handleNext();

            return;
        }

        if(QUESTIONNAIRE_TEXT_TAGS.includes(tagName)) {
            return;
        }

        handleShortcut(event);
    };

    const context = useMemo<QuestionnaireContextType>(() => ({
        handleChoiceChange,
        handleInputChange,
        handleChoiceMove,
        isReviewEnabled,
        submitErrorText,
        customLocalize,
        isSubmitFailed,
        registerChoice,
        registerInput,
        handleSubmit,
        activeIndex,
        customTheme,
        isReturning,
        isSubmitted,
        submittedAt,
        activeItem,
        contentRef,
        handleBack,
        handleEdit,
        handleNext,
        handleSkip,
        isDisabled,
        errorName,
        isLoading,
        isReview,
        swapStep,
        answers,
        items,
        title,
        size
    }), [
        handleChoiceChange,
        handleInputChange,
        handleChoiceMove,
        isReviewEnabled,
        submitErrorText,
        customLocalize,
        isSubmitFailed,
        registerChoice,
        registerInput,
        handleSubmit,
        activeIndex,
        customTheme,
        isReturning,
        isSubmitted,
        submittedAt,
        activeItem,
        contentRef,
        handleBack,
        handleEdit,
        handleNext,
        handleSkip,
        isDisabled,
        errorName,
        isLoading,
        isReview,
        swapStep,
        answers,
        items,
        title,
        size
    ]);

    const webProps = Platform.OS === "web" ? {
        onKeyDown
    } : {};

    const renderDefaultLayout = () => {
        if(isSubmitted) {
            return <Fragment>
                <QuestionnaireStatus />
                <QuestionnaireReview />
            </Fragment>;
        }

        return <Fragment>
            <QuestionnaireProgress />
            {
                isReview ?
                    <QuestionnaireReview />
                :
                    <QuestionnaireItem />
            }
            {
                isDisabled ?
                    <QuestionnaireStatus />
                :
                    <QuestionnaireActions />
            }
        </Fragment>;
    };

    return <QuestionnaireContext.Provider
        value={context}
    >
        <Card
            {...props}
            {...webProps}
            aria-label={isSubmitted && title ? `${title}, ${localize("questionnaire-submitted")}` : title}
            variant={isSubmitted ? "filled" : "outline"}
            role={isSubmitted ? "region" : "form"}
            customLocalize={customLocalize}
            customTheme={customTheme}
            style={style}
            size="small"
        >
            {children ?? renderDefaultLayout()}
        </Card>
    </QuestionnaireContext.Provider>;
};
export default Object.assign(Questionnaire, {
    Description: QuestionnaireDescription,
    Progress: QuestionnaireProgress,
    Actions: QuestionnaireActions,
    Choices: QuestionnaireChoices,
    Choice: QuestionnaireChoice,
    Review: QuestionnaireReview,
    Status: QuestionnaireStatus,
    Error: QuestionnaireError,
    Input: QuestionnaireInput,
    Title: QuestionnaireTitle,
    Item: QuestionnaireItem
});
